"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.EthereumTBTCVault = void 0;
const contracts_1 = require("../contracts");
const ethers_1 = require("ethers");
const bitcoin_1 = require("../bitcoin");
const utils_1 = require("../utils");
const adapter_1 = require("./adapter");
const address_1 = require("./address");
const bridge_1 = require("./bridge");
const TBTCVault_json_1 = __importDefault(require("./artifacts/mainnet/TBTCVault.json"));
const TBTCVault_json_2 = __importDefault(require("./artifacts/sepolia/TBTCVault.json"));
const TBTCVault_json_3 = __importDefault(require("@keep-network/tbtc-v2/artifacts/TBTCVault.json"));
/**
 * Implementation of the Ethereum TBTCVault handle.
 * @see {TBTCVault} for reference.
 */
class EthereumTBTCVault extends adapter_1.EthersContractHandle {
    constructor(config, chainId = contracts_1.Chains.Ethereum.Local) {
        let deployment;
        switch (chainId) {
            case contracts_1.Chains.Ethereum.Local:
                deployment = TBTCVault_json_3.default;
                break;
            case contracts_1.Chains.Ethereum.Sepolia:
                deployment = TBTCVault_json_2.default;
                break;
            case contracts_1.Chains.Ethereum.Mainnet:
                deployment = TBTCVault_json_1.default;
                break;
            default:
                throw new Error("Unsupported deployment type");
        }
        super(config, deployment);
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {TBTCVault#getChainIdentifier}
     */
    getChainIdentifier() {
        return address_1.EthereumAddress.from(this._instance.address);
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {TBTCVault#optimisticMintingDelay}
     */
    async optimisticMintingDelay() {
        const delaySeconds = await (0, utils_1.backoffRetrier)(this._totalRetryAttempts)(async () => {
            return await this._instance.optimisticMintingDelay();
        });
        return ethers_1.BigNumber.from(delaySeconds).toNumber();
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {TBTCVault#getMinters}
     */
    async getMinters() {
        const minters = await (0, utils_1.backoffRetrier)(this._totalRetryAttempts)(async () => {
            return await this._instance.getMinters();
        });
        return minters.map(address_1.EthereumAddress.from);
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {TBTCVault#isMinter}
     */
    async isMinter(address) {
        return await (0, utils_1.backoffRetrier)(this._totalRetryAttempts)(async () => {
            return await this._instance.isMinter(`0x${address.identifierHex}`);
        });
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {TBTCVault#isGuardian}
     */
    async isGuardian(address) {
        return await (0, utils_1.backoffRetrier)(this._totalRetryAttempts)(async () => {
            return await this._instance.isGuardian(`0x${address.identifierHex}`);
        });
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {TBTCVault#requestOptimisticMint}
     */
    async requestOptimisticMint(depositTxHash, depositOutputIndex) {
        const tx = await adapter_1.EthersTransactionUtils.sendWithRetry(async () => {
            return await this._instance.requestOptimisticMint(depositTxHash.reverse().toPrefixedString(), depositOutputIndex);
        }, this._totalRetryAttempts, undefined, [
            "Optimistic minting already requested for the deposit",
            "The deposit is already swept",
        ]);
        return utils_1.Hex.from(tx.hash);
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {TBTCVault#cancelOptimisticMint}
     */
    async cancelOptimisticMint(depositTxHash, depositOutputIndex) {
        const tx = await adapter_1.EthersTransactionUtils.sendWithRetry(async () => {
            return await this._instance.cancelOptimisticMint(depositTxHash.reverse().toPrefixedString(), depositOutputIndex);
        }, this._totalRetryAttempts, undefined, ["Optimistic minting already finalized for the deposit"]);
        return utils_1.Hex.from(tx.hash);
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {TBTCVault#finalizeOptimisticMint}
     */
    async finalizeOptimisticMint(depositTxHash, depositOutputIndex) {
        const tx = await adapter_1.EthersTransactionUtils.sendWithRetry(async () => {
            return await this._instance.finalizeOptimisticMint(depositTxHash.reverse().toPrefixedString(), depositOutputIndex);
        }, this._totalRetryAttempts, undefined, [
            "Optimistic minting already finalized for the deposit",
            "The deposit is already swept",
        ]);
        return utils_1.Hex.from(tx.hash);
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {TBTCVault#optimisticMintingRequests}
     */
    async optimisticMintingRequests(depositTxHash, depositOutputIndex) {
        const depositKey = bridge_1.EthereumBridge.buildDepositKey(depositTxHash, depositOutputIndex);
        const request = await (0, utils_1.backoffRetrier)(this._totalRetryAttempts)(async () => {
            return await this._instance.optimisticMintingRequests(depositKey);
        });
        return this.parseOptimisticMintingRequest(request);
    }
    /**
     * Parses a optimistic minting request using data fetched from the on-chain contract.
     * @param request Data of the optimistic minting request.
     * @returns Parsed optimistic minting request.
     */
    parseOptimisticMintingRequest(request) {
        return {
            requestedAt: ethers_1.BigNumber.from(request.requestedAt).toNumber(),
            finalizedAt: ethers_1.BigNumber.from(request.finalizedAt).toNumber(),
        };
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainBridge#getOptimisticMintingRequestedEvents}
     */
    async getOptimisticMintingRequestedEvents(options, ...filterArgs) {
        const events = await this.getEvents("OptimisticMintingRequested", options, ...filterArgs);
        return events.map((event) => {
            return {
                blockNumber: ethers_1.BigNumber.from(event.blockNumber).toNumber(),
                blockHash: utils_1.Hex.from(event.blockHash),
                transactionHash: utils_1.Hex.from(event.transactionHash),
                minter: address_1.EthereumAddress.from(event.args.minter),
                depositKey: utils_1.Hex.from(ethers_1.BigNumber.from(event.args.depositKey).toHexString()),
                depositor: address_1.EthereumAddress.from(event.args.depositor),
                amount: ethers_1.BigNumber.from(event.args.amount),
                fundingTxHash: bitcoin_1.BitcoinTxHash.from(event.args.fundingTxHash).reverse(),
                fundingOutputIndex: ethers_1.BigNumber.from(event.args.fundingOutputIndex).toNumber(),
            };
        });
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainBridge#getOptimisticMintingCancelledEvents}
     */
    async getOptimisticMintingCancelledEvents(options, ...filterArgs) {
        const events = await this.getEvents("OptimisticMintingCancelled", options, ...filterArgs);
        return events.map((event) => {
            return {
                blockNumber: ethers_1.BigNumber.from(event.blockNumber).toNumber(),
                blockHash: utils_1.Hex.from(event.blockHash),
                transactionHash: utils_1.Hex.from(event.transactionHash),
                guardian: address_1.EthereumAddress.from(event.args.guardian),
                depositKey: utils_1.Hex.from(ethers_1.BigNumber.from(event.args.depositKey).toHexString()),
            };
        });
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainBridge#getOptimisticMintingFinalizedEvents}
     */
    async getOptimisticMintingFinalizedEvents(options, ...filterArgs) {
        const events = await this.getEvents("OptimisticMintingFinalized", options, ...filterArgs);
        return events.map((event) => {
            return {
                blockNumber: ethers_1.BigNumber.from(event.blockNumber).toNumber(),
                blockHash: utils_1.Hex.from(event.blockHash),
                transactionHash: utils_1.Hex.from(event.transactionHash),
                minter: address_1.EthereumAddress.from(event.args.minter),
                depositKey: utils_1.Hex.from(ethers_1.BigNumber.from(event.args.depositKey).toHexString()),
                depositor: address_1.EthereumAddress.from(event.args.depositor),
                optimisticMintingDebt: ethers_1.BigNumber.from(event.args.optimisticMintingDebt),
            };
        });
    }
}
exports.EthereumTBTCVault = EthereumTBTCVault;
//# sourceMappingURL=tbtc-vault.js.map